/**
 * @file Notification d'alerte affichée lorsque le navigateur perd la connexion réseau.
 * @module packages.common.components.networkStatusBanner.NetworkStatusBanner
 *
 * Composant à monter une seule fois par application, au niveau racine sous le `ThemeProvider`.
 *
 *   - Snackbar persistant hors ligne : pill glassmorphism warning avec icône WiFi barrée,
 *     titre et sous-titre invitant l'utilisateur à vérifier sa connexion.
 *   - Snackbar success (~3 s) au retour en ligne avec icône WiFi, uniquement si l'utilisateur
 *     a réellement été déconnecté auparavant.
 *
 * Utilise directement `Snackbar` de MUI (pas notistack) pour deux raisons :
 *   1. notistack v3 ne forwarde pas correctement `sx` à ses internes (rendu en tant
 *      qu'attribut HTML brut) — impossible de contrôler proprement la position.
 *   2. Le banner réseau n'a pas besoin d'empilement avec d'autres notifications, donc
 *      les bénéfices de notistack (stack manager, dedup) ne s'appliquent pas ici.
 *
 * Positionnement responsive : sur mobile (< lg), le pill est placé à `bottomOffset` px
 * du bord bas, avec `env(safe-area-inset-bottom)` pour respecter les home indicators.
 * Sur desktop (≥ lg), position standard 24 px.
 *
 * La prop `bottomOffset` permet à chaque app de compenser sa propre nav mobile fixée
 * en bas : le dashboard passe `BOTTOM_NAV_HEIGHT` (hauteur du `BottomNav`), l'app public
 * laisse la valeur par défaut (0) puisqu'elle n'a pas de nav mobile fixée.
 *
 * Volontairement non bloquant : l'utilisateur peut continuer à naviguer sur le contenu
 * déjà chargé (utile en mode PWA avec service worker qui sert du cache).
 */

import {useEffect, useRef, useState} from 'react';
import {Box, Snackbar, Stack, Typography} from '@mui/material';
import {alpha} from '@mui/material/styles';
import WifiOffRoundedIcon from '@mui/icons-material/WifiOffRounded';
import WifiRoundedIcon from '@mui/icons-material/WifiRounded';
import {useTranslation} from 'react-i18next';
import {useNetworkStatus} from 'packages/common/hooks/useNetworkStatus';

const RECONNECT_TOAST_DURATION_MS = 3000;

const ANCHOR_BOTTOM_CENTER = {vertical: 'bottom', horizontal: 'center'};

const buildSnackbarSx = (bottomOffset) => ({
  bottom: {
    xs: `calc(${bottomOffset}px + env(safe-area-inset-bottom, 0px) + 16px)`,
    lg: 24,
  },
});

const pillSx = (severity) => ({
  backgroundColor: (theme) => alpha(theme.palette[severity].main, 0.92),
  color: 'common.white',
  px: 2.5,
  py: 1.25,
  borderRadius: 999,
  backdropFilter: 'blur(8px)',
  boxShadow: (theme) => theme.customShadows.z12,
});

/**
 * Bandeau d'état de connexion réseau.
 *
 * @param {Object} props
 * @param {number} [props.bottomOffset=0] - Décalage vertical en px pour esquiver une
 *   nav mobile fixée en bas (dashboard : `BOTTOM_NAV_HEIGHT`, public : `0`).
 * @return {JSX.Element}
 */
export const NetworkStatusBanner = ({bottomOffset = 0}) => {
  const {t} = useTranslation();
  const {isOnline} = useNetworkStatus();
  const [showReconnectedToast, setShowReconnectedToast] = useState(false);
  const wasOfflineRef = useRef(false);

  useEffect(() => {
    if (isOnline) {
      if (wasOfflineRef.current) {
        setShowReconnectedToast(true);
      }
    } else {
      setShowReconnectedToast(false);
    }
    wasOfflineRef.current = !isOnline;
  }, [isOnline]);

  const handleReconnectedClose = (_event, reason) => {
    if (reason === 'clickaway') return;
    setShowReconnectedToast(false);
  };

  const snackbarSx = buildSnackbarSx(bottomOffset);

  return (
    <>
      <Snackbar open={!isOnline} anchorOrigin={ANCHOR_BOTTOM_CENTER} sx={snackbarSx}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={pillSx('warning')}>
          <WifiOffRoundedIcon />
          <Box>
            <Typography variant="body2" sx={{fontWeight: 600, lineHeight: 1.2}}>
              {t('common.network.offline')}
            </Typography>
            <Typography variant="caption" sx={{opacity: 0.85, lineHeight: 1.2}}>
              {t('common.network.offlineHelp')}
            </Typography>
          </Box>
        </Stack>
      </Snackbar>

      <Snackbar
        open={showReconnectedToast}
        autoHideDuration={RECONNECT_TOAST_DURATION_MS}
        onClose={handleReconnectedClose}
        anchorOrigin={ANCHOR_BOTTOM_CENTER}
        sx={snackbarSx}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={pillSx('success')}>
          <WifiRoundedIcon />
          <Typography variant="body2" sx={{fontWeight: 600}}>
            {t('common.network.backOnline')}
          </Typography>
        </Stack>
      </Snackbar>
    </>
  );
};

export default NetworkStatusBanner;
